// Claude: Profile Visibility page — seller controls who can see their profile 10-03-2026
import React, { useEffect, useState } from "react";
import {
  Box, Flex, Text, VStack, Image, HStack, Button,
  Switch, FormControl, FormLabel, Spinner, Checkbox,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, useDisclosure,
} from "@chakra-ui/react";
import { Select } from "antd";
import { Images } from "../../Assets/images";
import { MdChevronLeft, MdOutlineWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { SellerAPI } from "../../Apis/sellerAPI";
import { AdminAPI } from "../../Apis/adminAPI";
import { DepartmentsAPI } from "../../Apis/departmentsAPI"; // Claude: department list 16-Apr-2026
import { filterCountries } from "../../Utils/countries";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const COUNTRY_OPTIONS = filterCountries("").map((c: any) => ({ label: c.value, value: c.value }));

// Claude: renamed Invisible → Restricted 30-03-2026; descriptions removed 24-04-2026
const MODES = [
  { value: "restricted", label: "Restricted" },
  { value: "targeted",   label: "Targeted" },
  { value: "open",       label: "Open" },
];

const ProfileVisibility = () => {
  const navigate = useNavigate();
  const { success, error } = useToastWrapper();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState("open");
  const [savedMode, setSavedMode] = useState("open"); // Claude: last saved/loaded mode — drives Save enable + Cancel 20-04-2026
  const [targetCountries, setTargetCountries] = useState<string[]>([]);
  const [targetIndustryIds, setTargetIndustryIds] = useState<string[]>([]);
  const [targetVerifiedOnly, setTargetVerifiedOnly] = useState(false);
  // Claude: targetBuyerList — "Include Existing Buyers List" checkbox 24-03-2026
  // When ticked, buyers already in the seller's BuyerSellerMapping are always shown
  // in addition to buyers who match the filter criteria (union, no duplicates).
  const [targetBuyerList, setTargetBuyerList] = useState(false);
  // Claude: targetDepartments — seller targets specific buyer departments in Targeted mode 16-Apr-2026
  const [targetDepartments, setTargetDepartments] = useState<string[]>([]);
  const [industryOptions, setIndustryOptions] = useState<{ label: string; value: string }[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]); // Claude: 16-Apr-2026
  const [isPlanActive, setIsPlanActive] = useState(false); // Claude: plan gate for visibility controls 12-03-2026
  const [hoveredMode, setHoveredMode] = useState<string | null>(null); // Claude: drives radio dot on hover 20-04-2026
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string | null>(null); // Claude: distinguishes no-plan vs expired 10-Apr-2026

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, industryRes, deptRes] = await Promise.all([
          SellerAPI.getSellerProfile(),
          AdminAPI.getIndustryTree(),
          DepartmentsAPI.getAll(), // Claude: 16-Apr-2026
        ]);
        const data = profileRes?.data?.data;
        if (data) {
          const planActive = !!(data.subscriptionPlanId && data.isPlanActive);
          setIsPlanActive(planActive);
          setSubscriptionPlanId(data.subscriptionPlanId ?? null); // Claude: 10-Apr-2026
          // Claude: force restricted display if no active plan 30-03-2026
          const initialMode = planActive ? (data.visibilityMode || "open") : "restricted";
          setMode(initialMode);
          setSavedMode(initialMode);
          setTargetCountries(data.targetCountries || []);
          setTargetIndustryIds(
            (data.targetIndustryIds || []).map((id: any) =>
              typeof id === "object" ? String(id._id) : String(id)
            )
          );
          setTargetVerifiedOnly(data.targetVerifiedOnly || false);
          setTargetBuyerList(data.targetBuyerList || false);
          // Claude: load targetDepartments — stored as ObjectIds 16-Apr-2026
          setTargetDepartments(
            (data.targetDepartments || []).map((id: any) =>
              typeof id === "object" ? String(id._id) : String(id)
            )
          );
        }
        const nodes: any[] = Array.isArray(industryRes?.data)
          ? industryRes.data
          : (industryRes?.data?.data ?? []);
        const flatten = (arr: any[]): any[] =>
          arr.flatMap((n: any) => [n, ...flatten(n.children || [])]);
        setIndustryOptions(
          flatten(nodes).map((n: any) => ({ label: n.name, value: String(n._id) }))
        );
        // Claude: department options 16-Apr-2026
        const deptList: any[] = Array.isArray(deptRes) ? deptRes : (deptRes?.data ?? []);
        setDepartmentOptions(deptList.map((d: any) => ({ label: d.name, value: String(d._id) })));
      } catch (err) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    // Claude: targeted mode requires at least one country or industry 23-03-2026
    if (mode === "targeted" && targetCountries.length === 0 && targetIndustryIds.length === 0 && targetDepartments.length === 0) {
      error("Please select at least one country, industry, or department for targeted visibility.");
      return;
    }
    // Claude: restricted warning shown on mode select — save directly 30-03-2026
    if (mode === "targeted") {
      onOpen();
    } else {
      await doSave();
    }
  };

  const doSave = async () => {
    onClose();
    setSaving(true);
    try {
      await SellerAPI.updateSellerProfile({
        visibilityMode: mode,
        targetIndustryIds: mode === "targeted" ? targetIndustryIds : [],
        targetCountries: mode === "targeted" ? targetCountries : [],
        targetVerifiedOnly: mode === "targeted" ? targetVerifiedOnly : false,
        // Claude: targetDepartments saved in Targeted mode only 16-Apr-2026
        targetDepartments: mode === "targeted" ? targetDepartments : [],
        // Claude: targetBuyerList saved as-is regardless of mode — criteria are stored settings
        // that only take effect when mode is Targeted. No need to reset on Open/Invisible. 24-03-2026
        targetBuyerList,
      });
      setSavedMode(mode);
      success("Profile visibility updated");
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box w="100%" display="flex" flexDirection="column" minH="100dvh">
      {/* Header */}
      <Flex w="100%" px="30px" py={5} bg="#0E122F" color="white"
        alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={2} cursor="pointer" onClick={() => navigate("/seller/myaccount")}>
          <MdChevronLeft size={22} color="white" />
          <Text fontSize="xl" fontWeight={700}>Profile Visibility</Text>
        </Flex>
        <VStack spacing={0} alignItems="center" justifyContent="center">
          <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
          <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">
            seller (beta)
          </Text>

        </VStack>
      </Flex>

      {loading ? (
        <Flex justifyContent="center" mt={16}><Spinner color="#FF5F12" /></Flex>
      ) : (
        <VStack spacing={0} w="100%" px={5} pt={5} pb={10} alignItems="stretch">

          {/* Claude: plan gate banner — no-plan vs expired messaging 10-Apr-2026 */}
          {!isPlanActive && (
            <Box mb={4} p={3} borderRadius="10px" bg="#FFF3CD" border="1px solid #FFEAA7">
              <Text fontSize="12px" color="#856404">
                {subscriptionPlanId ? (
                  <>Your subscription has expired. <Text as="span" textDecoration="underline" cursor="pointer" onClick={() => navigate("/seller/seller-subscriptions")}>Renew it on your Subscriptions page.</Text></>
                ) : (
                  <>Your profile is currently <strong>restricted</strong>. Contact your administrator to assign a subscription plan.</>
                )}
              </Text>
            </Box>
          )}

          {/* Claude: subtext above mode selector 28-03-2026 */}
          <Text fontSize="11px" color="gray.500" mb={3}>
            Choose who can see your profile (or) who you want to target. Clients/Buyers will be filtered by this.
          </Text>

          {/* Mode selector — horizontal row */}
          {/* Claude: isCurrent (savedMode) = dark bg + dot, stays until save; isChosen (mode≠saved) = green bg, no dot 20-04-2026 */}
          <HStack spacing={3} mb={6} alignItems="stretch">
            {MODES.map((m) => {
              const isCurrent = savedMode === m.value;
              const isChosen = mode === m.value && !isCurrent;
              return (
                <Box key={m.value} flex={1} p={3} borderRadius="12px"
                  cursor={isCurrent ? "default" : isPlanActive ? "pointer" : "not-allowed"}
                  border={isCurrent || isChosen ? "2px solid #0E122F" : "1px solid #E2E8F0"}
                  bg={isCurrent ? "#0E122F" : isChosen ? "#F0FFF4" : "white"}
                  _hover={!isCurrent && !isChosen && isPlanActive ? { bg: "#F0FFF4", borderColor: "#0E122F" } : {}}
                  transition="background 0.15s, border-color 0.15s"
                  opacity={!isCurrent && !isChosen && !isPlanActive ? 0.5 : 1}
                  onMouseEnter={() => { if (!isCurrent && isPlanActive) setHoveredMode(m.value); }}
                  onMouseLeave={() => setHoveredMode(null)}
                  onClick={() => {
                    if (!isPlanActive || isCurrent) return;
                    setMode(m.value);
                  }}
                  textAlign="center">
                  {/* Claude: dot shown for current (white on dark) + hovered/chosen (dark on light) 20-04-2026 */}
                  <Box w="14px" h="14px" borderRadius="50%" border="2px solid" mx="auto" mb={2}
                    borderColor={isCurrent ? "white" : isChosen || hoveredMode === m.value ? "#0E122F" : "gray.300"}
                    bg={isCurrent ? "#0E122F" : "white"}
                    display="flex" alignItems="center" justifyContent="center">
                    {(isCurrent || isChosen || hoveredMode === m.value) && (
                      <Box w="5px" h="5px" borderRadius="50%" bg={isCurrent ? "white" : "#0E122F"} />
                    )}
                  </Box>
                  <Text fontSize="sm" fontWeight={600} color={isCurrent ? "white" : "#0E122F"}>{m.label}</Text>
                </Box>
              );
            })}
          </HStack>

          {/* Claude: Restricted info box — shown on select or when already saved as restricted 24-04-2026 */}
          {mode === "restricted" && (
            <Box mb={6} p={4} borderRadius="12px" bg="#FFF8E1" border="1px solid #FFE082">
              <Flex alignItems="center" gap={2} mb={2}>
                <MdOutlineWarning size={18} color="#C62828" />
                <Text fontSize="sm" fontWeight={600} color="#7B5E00">
                  {savedMode !== "restricted" ? "Switching to Restricted will:" : "You are currently in Restricted mode"}
                </Text>
              </Flex>
              {[
                { text: "Your profile is hidden from new buyer searches.", green: false },
                { text: "Your assets are hidden from new buyer searches.", green: false },
                { text: "New meeting requests are blocked.", green: false },
                { text: "New RFX matches are blocked.", green: false },
                { text: "Your existing confirmed meetings are not affected.", green: true },
                { text: "Your existing RFX responses are not affected.", green: true },
              ].map((item, i) => (
                <Flex key={i} gap={2} alignItems="flex-start" mb={1}>
                  <Text fontSize="sm" color={item.green ? "green.600" : "red.500"} fontWeight={700} flexShrink={0}>•</Text>
                  <Text fontSize="sm" color="gray.700">{item.text}</Text>
                </Flex>
              ))}
            </Box>
          )}

          {/* Claude: Targeted mode info hint — above criteria 20-04-2026 */}
          {(mode === "targeted" || hoveredMode === "targeted") && (
            <Box mb={4} p={4} borderRadius="12px" bg="#EBF8FF" border="1px solid #BEE3F8">
              <Text fontSize="sm" color="gray.700">
                When visibility mode is set to targeted, you can set filters for which specific buyer enquiries are shown to you.
              </Text>
            </Box>
          )}

          {/* Targeted criteria — only when targeted */}
          {mode === "targeted" && (
            <VStack spacing={4} alignItems="stretch" mb={6}
              p={4} bg="gray.50" borderRadius="12px" border="1px solid #E2E8F0">
              <Text fontSize="sm" fontWeight={600} color="#0E122F">Targeting Criteria</Text>
              <Text fontSize="11px" color="red.400" mt={-2}>At least one country, industry, or department is required.</Text>


              <Box>
                <Text fontSize="11px" color="gray.500" mb={1}>Target Countries</Text>
                <Select mode="multiple" showSearch optionFilterProp="label"
                  style={{ width: "100%" }} placeholder="Any country"
                  options={COUNTRY_OPTIONS}
                  value={targetCountries}
                  onChange={(v: string[]) => setTargetCountries(v)} />
              </Box>

              <Box>
                <Text fontSize="11px" color="gray.500" mb={1}><Text as="span" color="gray.400">(and) </Text>Target Industries</Text>
                <Select mode="multiple" showSearch optionFilterProp="label"
                  style={{ width: "100%" }} placeholder="Any industry"
                  options={industryOptions}
                  value={targetIndustryIds}
                  onChange={(v: string[]) => setTargetIndustryIds(v)} />
              </Box>

              {/* Claude: targetDepartments — buyer departments to target 16-Apr-2026 */}
              <Box>
                <Text fontSize="11px" color="gray.500" mb={1}><Text as="span" color="gray.400">(and) </Text>Target Departments</Text>
                <Select mode="multiple" showSearch optionFilterProp="label"
                  style={{ width: "100%" }} placeholder="Any department"
                  options={departmentOptions}
                  value={targetDepartments}
                  onChange={(v: string[]) => setTargetDepartments(v)} />
                <Text fontSize="10px" color="gray.400" mt={1}>
                  If your department is not listed, please contact support to add it.
                </Text>
              </Box>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" color="gray.700" mb={0} flex={1}>
                  Verified buyers only
                </FormLabel>
                <Switch colorScheme="purple" isChecked={targetVerifiedOnly}
                  onChange={(e) => setTargetVerifiedOnly(e.target.checked)} />
              </FormControl>

              {/* Claude: "Include Existing Buyers List" checkbox — 24-03-2026
                  When ticked, buyers already connected to this seller (via RFX, meeting, or direct)
                  are always shown this seller's profile, on top of the filter results above.
                  Example: filters return 25 buyers + 10 in the list (3 overlap) = 32 unique buyers see the seller.
                  When unticked, those 10 buyers must still pass the country/industry/verified filters. */}
              <Checkbox
                isChecked={targetBuyerList}
                onChange={(e) => setTargetBuyerList(e.target.checked)}
                colorScheme="purple"
                size="md"
              >
                <Text fontSize="sm" color="gray.700">Include Existing Buyers List</Text>
              </Checkbox>
            </VStack>
          )}

          {/* Claude: Open mode hover hint — matches restricted box style 20-04-2026 */}
          {(mode === "open" || hoveredMode === "open") && (
            <Box mb={6} p={4} borderRadius="12px" bg="#EBF8FF" border="1px solid #BEE3F8">
              <Text fontSize="sm" color="gray.700">
                An open profile means all buyer enquiries that match your profile in terms of country, industry and tags are shown to you, up to the subscription plan limits. Buyers are not filtered.
              </Text>
              <Text fontSize="sm" color="gray.700" mt={3}>
                While you might get access to more buyer queries as compared to a targeted mode, the subscription limits may also be reached faster.
              </Text>
            </Box>
          )}

          {/* Claude: Save + Cancel — Cancel reverts to last saved mode; Save enabled only on mode change 20-04-2026 */}
          <HStack spacing={3}>
            {mode !== savedMode && (
              <Button flex={1} variant="outline" borderRadius="10px" borderColor="#0E122F"
                color="#0E122F" onClick={() => setMode(savedMode)} isDisabled={saving}>
                Cancel
              </Button>
            )}
            <Button flex={1} bg="#0E122F" color="white" borderRadius="10px"
              _hover={{ bg: "#159300" }} onClick={handleSave}
              isLoading={saving}
              isDisabled={saving || !isPlanActive || mode === savedMode}>
              Save
            </Button>
          </HStack>
        </VStack>
      )}

      {/* Claude: Targeted save confirmation modal 25-03-2026; restricted branch removed 24-04-2026 */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4} borderRadius="16px">
          <ModalHeader fontSize="md" fontWeight={700}>Confirm Visibility Change</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.700">
              You are switching to <strong>Targeted</strong> visibility. Only buyers who match
              your selected criteria will be able to see your profile.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button size="sm" variant="ghost" isLoading={saving} onClick={doSave}>
              Proceed anyway
            </Button>
            <Button size="sm" bg="#C62828" color="white" _hover={{ bg: "#B71C1C" }} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileVisibility;
